import { useEffect, useRef, useState } from 'react';
import { isSome } from 'fp-ts/lib/Option';
import { useLocation } from 'react-router-dom';

import { useCase } from 'adapter/in/common/hooks/useCase';
import { useProfile } from 'adapter/in/common/context/static/Profile';
import { useWidgetDataDependencies, useWidgetDataCriteriaDependencies } from 'adapter/in/common/commonDependencies';
import { ReadAnalysisCriteriaBuilder } from 'use-case/analysis/read/readAnalysisCriteria';
import { CompareTimeshift, useAiTextGeneration } from 'adapter/in/common/analysis/analysisHooks';
import {
    isAiDescriptionAllowedDeployment,
    isAiDescriptionFeatureGranted,
    readLicenseFeaturesOnce,
} from 'adapter/in/common/analysis/aiDescriptionGate';

const useReadOnlyAiDescription = (analysisId: string, isAnalysisWidget: boolean): { text: string; isGenerating: boolean } => {
    const { profile } = useProfile();
    const { getReadAnalysis, previewAnalysisData, getReadLicensePermissions } = useWidgetDataDependencies();
    const { registerWidget } = useWidgetDataCriteriaDependencies();
    const { data: analysisResult, error: configError, execute: readAnalysis } = useCase(getReadAnalysis, false);
    const context = new URLSearchParams(useLocation().search).get('context');
    const { aiDescriptionText, isGenerating, hasError, hasResult, generate } = useAiTextGeneration(previewAnalysisData);
    const generatedForAnalysis = useRef<string | null>(null);

    const allowedByDeployment = isAnalysisWidget && isAiDescriptionAllowedDeployment(profile.deploymentId);
    const [allowedByLicense, setAllowedByLicense] = useState(false);

    useEffect(() => {
        if (!isAnalysisWidget || allowedByDeployment) {
            return undefined;
        }
        let cancelled = false;
        readLicenseFeaturesOnce(getReadLicensePermissions).then((features) => {
            if (!cancelled) {
                setAllowedByLicense(isAiDescriptionFeatureGranted(features));
            }
        });
        return () => {
            cancelled = true;
        };
    }, [isAnalysisWidget, allowedByDeployment]);

    const isFeatureAllowed = allowedByDeployment || (isAnalysisWidget && allowedByLicense);

    useEffect(() => {
        if (isFeatureAllowed && analysisId) {
            readAnalysis(ReadAnalysisCriteriaBuilder.create().withAnalysisId(analysisId).build());
        }
    }, [isFeatureAllowed, analysisId]);

    const analysis = isSome(analysisResult) ? analysisResult.value : null;
    const isEnabled = analysis?.visualisation.enableAiDescription ?? false;

    useEffect(() => {
        if (!analysis || !isEnabled || generatedForAnalysis.current === analysisId) {
            return;
        }
        generatedForAnalysis.current = analysisId;
        const config = {
            resolution: analysis.resolution,
            period: analysis.period,
            compareTimeshift: analysis.compareTimeshift as CompareTimeshift,
            visualisation: analysis.visualisation,
            atomsVisualisation: analysis.atomsVisualisation ?? [],
            presets: analysis.presets,
        };
        generate(config, analysisId, context, analysis.visualisation.userPrompt ?? '');
    }, [analysis, isEnabled, analysisId, context]);

    const [markLoaded, setMarkLoaded] = useState<(() => void) | undefined>();

    useEffect(() => {
        if (isFeatureAllowed) {
            const markWidgetLoaded = registerWidget?.();
            setMarkLoaded(() => markWidgetLoaded);
        }
    }, [isFeatureAllowed]);

    const configSettled = isSome(analysisResult) || isSome(configError);
    const ready = !isFeatureAllowed || (configSettled && !isEnabled) || (isEnabled && (hasResult || hasError));

    useEffect(() => {
        if (ready) {
            markLoaded?.();
        }
    }, [ready, markLoaded]);

    return {
        text: isEnabled ? aiDescriptionText ?? '' : '',
        isGenerating: isEnabled && isGenerating,
    };
};

export default useReadOnlyAiDescription;
